/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshButton from './ui/RefreshButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Switch } from './ui/switch';
import {
  Coins, Package, Gift, Clock, Zap, Users, TrendingUp, TrendingDown,
  Plus, Edit, Trash2, Eye, Search, Filter, Download, RefreshCw,
  UserPlus, Cake, Award, ShoppingCart, FileText, Key, AlertTriangle,
  CheckCircle, XCircle, Calendar, Timer, Percent, Hash, ArrowUpRight,
  ArrowDownRight, BarChart3, PieChart, Activity, Settings, Star, CreditCard,
  HelpCircle, BookOpen
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import StripeCheckoutButton from './StripeCheckoutButton';
import Sparkline from './credits/Sparkline';
import CreditUsagePlansTab from './CreditUsagePlansTab';
import CreditStatsAdvancedTab from './CreditStatsAdvancedTab';
import CreditAlertsAdminTab from './CreditAlertsAdminTab';


export const GCAttributeModal = ({ ctx }) => {
  const {
    actions, activeTab, attributeForm, attributeSearch, bonusForm, bonusRules,
    bulkAttributeForm, creditTypes, duplicateRaison, duplicateSource, duplicateTargetSearch,
    duplicateTargetUserId, editForm, editTarget, editingBonus, editingPack, loading,
    packForm, packs, searchTerm, selectedUserIds, showAttributeModal, showBonusModal,
    showBulkAttributeModal, showDuplicateModal, showEditModal, showPackModal, sparkSeries, stats,
    transactions, userBalances,
    setActions, setActiveTab, setAttributeForm, setAttributeSearch, setBonusForm, setBonusRules,
    setBulkAttributeForm, setCreditTypes, setDuplicateRaison, setDuplicateSource, setDuplicateTargetSearch,
    setDuplicateTargetUserId, setEditForm, setEditTarget, setEditingBonus, setEditingPack,
    setLoading, setPackForm, setPacks, setSearchTerm, setSelectedUserIds, setShowAttributeModal,
    setShowBonusModal, setShowBulkAttributeModal, setShowDuplicateModal, setShowEditModal, setShowPackModal,
    setSparkSeries, setStats, setTransactions, setUserBalances,
    exportUsersCsv, filteredUsers, handleAttributeCredits, handleBulkAttribute, handleBulkDelete,
    handleConfirmDuplicate, handleDeletePack, handleDeleteUserCredits, handleExportFiltered,
    handleExportSelection, handleSaveBonus, handleSaveEdit, handleSavePack, loadData, openDuplicateModal,
    openEditModal, renderIcon, resetPackForm, toggleSelectAllFiltered, toggleUserSelection,
    getAuthHeader, CountdownWidget, StatsCard, COLORS, AVAILABLE_ICONS,
  } = ctx;
  return (
      <Dialog open={showAttributeModal} onOpenChange={setShowAttributeModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-blue-500" />
              Attribuer des crédits manuellement
            </DialogTitle>
            <DialogDescription>
              Geste commercial, compensation ou fidélisation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Utilisateur *</Label>
              <Input
                placeholder="Rechercher par email, nom ou prénom (incl. comptes test)…"
                value={attributeSearch}
                onChange={(e) => {
                  setAttributeSearch(e.target.value);
                  // Réinitialise la sélection dès que l'utilisateur retape
                  if (attributeForm.user_id) {
                    setAttributeForm({ ...attributeForm, user_id: '' });
                  }
                }}
                className="mb-2"
                data-testid="attribute-credits-search"
                autoComplete="off"
              />

              {/* Liste dynamique de résultats — ne s'affiche que si recherche active sans sélection */}
              {attributeSearch && !attributeForm.user_id && (
                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y" data-testid="attribute-credits-results">
                  {(() => {
                    const q = attributeSearch.toLowerCase().trim();
                    const matches = userBalances.filter((u) => {
                      const info = u.user_info || {};
                      return (
                        (info.email || '').toLowerCase().includes(q) ||
                        (info.nom || '').toLowerCase().includes(q) ||
                        (info.prenom || '').toLowerCase().includes(q)
                      );
                    }).slice(0, 50);

                    if (matches.length === 0) {
                      return (
                        <div className="px-3 py-3 text-sm text-slate-500 text-center">
                          Aucun utilisateur trouvé
                        </div>
                      );
                    }

                    return matches.map((u) => (
                      <button
                        key={u.user_id}
                        type="button"
                        onClick={() => {
                          setAttributeForm({ ...attributeForm, user_id: u.user_id });
                          setAttributeSearch(
                            `${u.user_info?.is_test ? '🧪 ' : ''}${u.user_info?.prenom || ''} ${u.user_info?.nom || ''} (${u.user_info?.email || ''})`.trim()
                          );
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center justify-between gap-2"
                        data-testid={`attribute-credits-result-${u.user_id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-800 truncate">
                            {u.user_info?.is_test ? '🧪 ' : ''}
                            {u.user_info?.prenom} {u.user_info?.nom}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {u.user_info?.email}
                            {u.user_info?.role ? ` — ${u.user_info.role}` : ''}
                          </div>
                        </div>
                        <Badge variant="outline" className="bg-slate-900 text-white text-xs font-semibold">
                          {u.total_disponible || 0}
                        </Badge>
                      </button>
                    ));
                  })()}
                </div>
              )}

              {/* Confirmation visuelle de la sélection */}
              {attributeForm.user_id && (
                <div className="flex items-center justify-between gap-2 mt-1 p-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="attribute-credits-selected">
                  <div className="flex items-center gap-2 text-sm text-emerald-800">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Utilisateur sélectionné</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setAttributeForm({ ...attributeForm, user_id: '' });
                      setAttributeSearch('');
                    }}
                    className="text-emerald-700 hover:text-emerald-900 h-7"
                    data-testid="attribute-credits-clear"
                  >
                    Changer
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre de crédits *</Label>
                <Input
                  type="number"
                  min="1"
                  value={attributeForm.credits}
                  onChange={(e) => setAttributeForm({...attributeForm, credits: parseInt(e.target.value) || 0})}
                />
              </div>

              <div>
                <Label>Type de crédit</Label>
                <Select 
                  value={attributeForm.type_credit} 
                  onValueChange={(v) => setAttributeForm({...attributeForm, type_credit: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BONUS">Bonus (cadeau)</SelectItem>
                    <SelectItem value="STANDARD">Standard</SelectItem>
                    <SelectItem value="TEMPORARY">Temporaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {attributeForm.type_credit === 'TEMPORARY' && (
              <div>
                <Label>Durée de validité (jours)</Label>
                <Input
                  type="number"
                  min="1"
                  value={attributeForm.duree_validite_jours || ''}
                  onChange={(e) => setAttributeForm({...attributeForm, duree_validite_jours: parseInt(e.target.value) || null})}
                  placeholder="30"
                />
              </div>
            )}

            <div>
              <Label>Raison / Motif *</Label>
              <Textarea
                value={attributeForm.raison}
                onChange={(e) => setAttributeForm({...attributeForm, raison: e.target.value})}
                placeholder="Ex: Geste commercial suite réclamation #12345"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAttributeModal(false)}>Annuler</Button>
            <Button onClick={handleAttributeCredits} className="bg-blue-600 hover:bg-blue-700">
              <Gift className="w-4 h-4 mr-2" />
              Attribuer {attributeForm.credits} crédits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};

export default GCAttributeModal;
